import React from "react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import * as Icons from "lucide-react";
import { cn } from "@/lib/utils";

export default function QuestionStep({ question, value, onChange, extra, onExtraChange }) {
  const Icon = Icons[question.icon] || Icons.HelpCircle;

  return (
    <div data-testid={`question-${question.key}`} className="w-full">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-12 h-12 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-1 leading-tight">
            {question.label}
          </h2>
          {question.hint && (
            <p className="text-sm text-muted-foreground">{question.hint}</p>
          )}
        </div>
      </div>

      {question.type === "single" && (
        <>
          <RadioGroup
            value={value || ""}
            onValueChange={(v) => {
              onChange(v);
              // clear exact field when user picks a bucket
              if (question.exactField && onExtraChange) onExtraChange("");
            }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-3"
          >
            {question.options.map((opt) => {
              const selected = value === opt.value && !(question.exactField && extra);
              return (
                <Label
                  key={opt.value}
                  htmlFor={`${question.key}-${opt.value}`}
                  data-testid={`option-${question.key}-${opt.value}`}
                  className={cn(
                    "flex items-center gap-3 rounded-lg border p-4 cursor-pointer tile-hover",
                    selected
                      ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                      : "border-border bg-secondary/40 hover:bg-secondary/60"
                  )}
                >
                  <RadioGroupItem
                    id={`${question.key}-${opt.value}`}
                    value={opt.value}
                    className="data-[state=checked]:border-primary data-[state=checked]:text-primary"
                  />
                  <span className="text-sm md:text-base">{opt.label}</span>
                </Label>
              );
            })}
          </RadioGroup>

          {question.exactField && (
            <div className="mt-5 rounded-lg border border-dashed border-border bg-secondary/20 p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex-1">
                  <div className="text-sm font-medium">Oder exakte Anzahl angeben</div>
                  <div className="text-xs text-muted-foreground">Z. B. 78 oder 145 – überschreibt die Bereichs-Auswahl.</div>
                </div>
                <Input
                  data-testid={`input-${question.exactField}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100000}
                  value={extra || ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^0-9]/g, "");
                    if (onExtraChange) onExtraChange(raw);
                    // clear bucket radio when user types a number
                    if (raw && value) onChange(null);
                  }}
                  placeholder="Anzahl Kameras"
                  className="w-full sm:w-48 bg-background border-border"
                />
              </div>
            </div>
          )}
        </>
      )}

      {question.type === "boolean" && (
        <div className="grid grid-cols-2 gap-3">
          {[
            { v: true, label: "Ja" },
            { v: false, label: "Nein" },
          ].map((b) => {
            const selected = value === b.v;
            return (
              <button
                key={String(b.v)}
                type="button"
                data-testid={`option-${question.key}-${b.v ? "yes" : "no"}`}
                onClick={() => onChange(b.v)}
                className={cn(
                  "rounded-lg border p-5 text-center tile-hover font-medium",
                  selected
                    ? "border-primary bg-primary/10 text-primary ring-1 ring-primary/40"
                    : "border-border bg-secondary/40 text-foreground hover:bg-secondary/60"
                )}
              >
                {b.label}
              </button>
            );
          })}
        </div>
      )}

      {question.type === "multi" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {question.subOptions.map((sub) => {
            const checked = !!(value && value[sub.value]);
            return (
              <Label
                key={sub.value}
                htmlFor={`${question.key}-${sub.value}`}
                data-testid={`option-${question.key}-${sub.value}`}
                className={cn(
                  "flex items-center gap-3 rounded-lg border p-4 cursor-pointer tile-hover",
                  checked
                    ? "border-primary bg-primary/10 ring-1 ring-primary/40"
                    : "border-border bg-secondary/40 hover:bg-secondary/60"
                )}
              >
                <Checkbox
                  id={`${question.key}-${sub.value}`}
                  checked={checked}
                  onCheckedChange={(v) =>
                    onChange({ ...(value || {}), [sub.value]: !!v })
                  }
                  className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                />
                <span className="text-sm md:text-base">{sub.label}</span>
              </Label>
            );
          })}
        </div>
      )}
    </div>
  );
}
